import React, { useEffect } from 'react';
import { ArrowLeft, Users, Shield, FileSearch, CheckCircle, AlertTriangle, BarChart3 } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const ThirdPartyAuditsPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="dark-container">
      <Header />
      
      {/* Hero Section */}
      <section className="service-hero">
        <div className="dark-content-container">
          <Link to="/" className="back-link">
            <ArrowLeft size={20} />
            Back to Home
          </Link>
          
          <div className="service-hero-content">
            <div className="service-badge">
              <Users className="service-badge-icon" />
              <span>Third-Party Security Audits</span>
            </div>
            
            <h1 className="service-title">
              Vendor Risk Management & 
              <span className="service-title-accent"> Supply Chain Security</span>
            </h1>
            
            <p className="service-description">
              Comprehensive third-party security assessments and vendor risk evaluations to 
              protect your organization from supply chain vulnerabilities and ensure vendor 
              security compliance across your ecosystem.
            </p>
            
            <div className="service-stats">
              <div className="service-stat">
                <span className="service-stat-number">78%</span>
                <span className="service-stat-label">Supply Chain Risks Identified</span>
              </div>
              <div className="service-stat">
                <span className="service-stat-number">500+</span>
                <span className="service-stat-label">Vendor Assessments Completed</span>
              </div>
              <div className="service-stat">
                <span className="service-stat-number">$2.1M</span>
                <span className="service-stat-label">Average Risk Exposure Reduced</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Risk Categories */}
      <section className="risk-categories-section">
        <div className="dark-content-container">
          <h2 className="display-large">Third-Party Risk Categories</h2>
          
          <div className="categories-grid">
            <div className="category-card">
              <Shield className="category-icon" />
              <h3 className="heading-2">Cybersecurity Risk</h3>
              <p className="body-medium">
                Assessment of vendor cybersecurity controls, incident response capabilities, 
                and data protection measures to prevent security breaches.
              </p>
              <ul className="category-features">
                <li>Security framework compliance (SOC 2, ISO 27001)</li>
                <li>Incident response and breach notification</li>
                <li>Data encryption and protection standards</li>
                <li>Vulnerability management programs</li>
                <li>Access controls and authentication</li>
              </ul>
            </div>

            <div className="category-card">
              <FileSearch className="category-icon" />
              <h3 className="heading-2">Compliance Risk</h3>
              <p className="body-medium">
                Evaluation of regulatory compliance status and adherence to industry 
                standards relevant to your business requirements.
              </p>
              <ul className="category-features">
                <li>GDPR and data privacy compliance</li>
                <li>Industry-specific regulations (HIPAA, PCI DSS)</li>
                <li>International standards (ISO, NIST)</li>
                <li>Audit trail and documentation review</li>
                <li>Compliance monitoring and reporting</li>
              </ul>
            </div>

            <div className="category-card">
              <BarChart3 className="category-icon" />
              <h3 className="heading-2">Operational Risk</h3>
              <p className="body-medium">
                Analysis of business continuity, disaster recovery capabilities, 
                and operational resilience of vendor services.
              </p>
              <ul className="category-features">
                <li>Business continuity and disaster recovery</li>
                <li>Service level agreement compliance</li>
                <li>Geographic and infrastructure dependencies</li>
                <li>Operational stability and performance</li>
                <li>Change management processes</li>
              </ul>
            </div>

            <div className="category-card">
              <AlertTriangle className="category-icon" />
              <h3 className="heading-2">Financial Risk</h3>
              <p className="body-medium">
                Assessment of vendor financial stability, insurance coverage, 
                and contractual risk exposure.
              </p>
              <ul className="category-features">
                <li>Financial stability and viability</li>
                <li>Insurance coverage adequacy</li>
                <li>Contractual terms and liability</li>
                <li>Intellectual property protection</li>
                <li>Termination and transition planning</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Assessment Methodology */}
      <section className="methodology-section">
        <div className="dark-content-container">
          <h2 className="display-large">Comprehensive Assessment Methodology</h2>
          
          <div className="methodology-grid">
            <div className="methodology-step">
              <div className="step-number">01</div>
              <div className="step-content">
                <h3 className="heading-2">Vendor Inventory & Classification</h3>
                <p className="body-medium">
                  Complete vendor ecosystem mapping with risk-based classification 
                  to prioritize assessment efforts on critical suppliers.
                </p>
                <ul className="step-features">
                  <li>Vendor discovery and cataloging</li>
                  <li>Risk-based vendor tiering</li>
                  <li>Critical vendor identification</li>
                  <li>Data access level classification</li>
                </ul>
              </div>
            </div>

            <div className="methodology-step">
              <div className="step-number">02</div>
              <div className="step-content">
                <h3 className="heading-2">Security Questionnaires & Documentation</h3>
                <p className="body-medium">
                  Standardized security assessments using industry-recognized 
                  questionnaires and documentation review processes.
                </p>
                <ul className="step-features">
                  <li>SIG Lite and custom questionnaires</li>
                  <li>Security certification review</li>
                  <li>Policy and procedure analysis</li>
                  <li>Incident history evaluation</li>
                </ul>
              </div>
            </div>

            <div className="methodology-step">
              <div className="step-number">03</div>
              <div className="step-content">
                <h3 className="heading-2">On-Site Security Audits</h3>
                <p className="body-medium">
                  Physical and virtual security assessments for high-risk vendors 
                  including facility reviews and technical testing.
                </p>
                <ul className="step-features">
                  <li>Physical security assessments</li>
                  <li>Technical control validation</li>
                  <li>Personnel and process reviews</li>
                  <li>Vulnerability scanning and testing</li>
                </ul>
              </div>
            </div>

            <div className="methodology-step">
              <div className="step-number">04</div>
              <div className="step-content">
                <h3 className="heading-2">Continuous Monitoring</h3>
                <p className="body-medium">
                  Ongoing vendor risk monitoring with automated tools and 
                  regular reassessments to maintain current risk profiles.
                </p>
                <ul className="step-features">
                  <li>Threat intelligence monitoring</li>
                  <li>Security rating services integration</li>
                  <li>Breach notification tracking</li>
                  <li>Periodic reassessment scheduling</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vendor Risk Management Platform */}
      <section className="platform-section">
        <div className="dark-content-container">
          <h2 className="display-large">Vendor Risk Management Platform</h2>
          
          <div className="platform-grid">
            <div className="platform-content">
              <h3 className="heading-2">Centralized Risk Visibility & Control</h3>
              <p className="body-large">
                Comprehensive vendor risk management platform providing real-time 
                visibility into your supplier ecosystem with automated risk scoring 
                and continuous monitoring capabilities.
              </p>
              
              <div className="platform-features">
                <div className="platform-feature">
                  <CheckCircle className="feature-icon" />
                  <div>
                    <h4 className="heading-3">Automated Risk Scoring</h4>
                    <p className="body-small">AI-powered risk assessment with continuous scoring updates</p>
                  </div>
                </div>
                
                <div className="platform-feature">
                  <BarChart3 className="feature-icon" />
                  <div>
                    <h4 className="heading-3">Risk Dashboard & Reporting</h4>
                    <p className="body-small">Executive dashboards with drill-down capabilities and custom reports</p>
                  </div>
                </div>
                
                <div className="platform-feature">
                  <AlertTriangle className="feature-icon" />
                  <div>
                    <h4 className="heading-3">Breach & Incident Monitoring</h4>
                    <p className="body-small">Real-time notifications of vendor security incidents and breaches</p>
                  </div>
                </div>

                <div className="platform-feature">
                  <FileSearch className="feature-icon" />
                  <div>
                    <h4 className="heading-3">Compliance Tracking</h4>
                    <p className="body-small">Automated compliance monitoring and certification expiry tracking</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="platform-stats">
              <div className="stat-item">
                <span className="stat-number">50%</span>
                <span className="stat-label">Reduction in Assessment Time</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">99.5%</span>
                <span className="stat-label">Risk Detection Accuracy</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">24/7</span>
                <span className="stat-label">Continuous Monitoring</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ThirdPartyAuditsPage;